/**
 * Master Data Management (MDM) Manager Page - REFACTORED
 *
 * Purpose: Main interface for managing master data values across field definitions and tenants
 * Architecture: React page component using extracted hooks and components
 * Dependencies: React, Material-UI, MDM components, hooks, and services
 * Author: Development Team
 * Created: 2025-01-27
 * Refactored: 2025-01-21
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Typography,
  SelectChangeEvent,
} from '@mui/material';
import {
  Home as HomeIcon,
  Settings as SettingsIcon,
  Storage as StorageIcon,
  Public as GlobalIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import SkeletonPage from '../../components/layout/SkeletonPage';
import { useAuth } from '../../context/AuthContext';
import { PermissionKey } from '../../constants/permissionKeys';
import { mdmService } from '../../services/mdmService';
import { DismissibleBanner } from '../../components/common/DismissibleBanner';
import { MDMManagerProps } from '../../types/mdm';
import ValueFormModal from '../../components/mdm/ValueFormModal';
import AliasManagerDrawer from '../../components/mdm/AliasManagerDrawer';
import ImportModal from '../../components/mdm/ImportModal';
import ExportDialog from '../../components/mdm/ExportDialog';
import RevertOverrideDialog from '../../components/mdm/RevertOverrideDialog';
import MDMControls from '../../components/mdm/MDMControls';
import MDMFilters from '../../components/mdm/MDMFilters';
import MDMValueTable from '../../components/mdm/MDMValueTable';
import { MasterDataValue } from '../../types/mdm';
import { useMDMData, useMDMFilters, useMDMDialogs } from '../../hooks/useMDMManager';
import {
  getDropdownFieldDefinitions,
  filterValues,
  isValidSelection,
  canDeleteValue,
  isSourceField,
} from '../../utils/mdmHelpers';

/**
 * MDM Manager Page Component
 *
 * Purpose: Main interface for master data management operations
 * Features: Field selection, tenant context switching, value management, import/export
 */
const MDMManagerPage: React.FC<MDMManagerProps> = ({ initialFieldDefId, initialTenantId }) => {
  const { state, can } = useAuth();
  const { user } = state;
  // Read-only holders of the MDM Manager see values but no write controls (ORCH-334)
  const canEditMdm = can(PermissionKey.MDM_EDIT);
  const canManageGlobalAliases =
    user?.activeTenant?.tenant_id === 'GLOBAL' && can(PermissionKey.MDM_MANAGE_ALIASES);

  // ORCH-115: alias drawer state. The drawer is opened from the MDMValueTable
  // aliases column or from ValueFormModal's edit-mode "Manage aliases" link.
  const [aliasDrawerOpen, setAliasDrawerOpen] = useState<boolean>(false);
  const [aliasDrawerValue, setAliasDrawerValue] = useState<MasterDataValue | null>(null);
  const handleManageAliases = useCallback((value: MasterDataValue) => {
    setAliasDrawerValue(value);
    setAliasDrawerOpen(true);
  }, []);
  const handleCloseAliasDrawer = useCallback(() => {
    setAliasDrawerOpen(false);
  }, []);

  // Page configuration
  const pageConfig = {
    pageTitle: 'Master Data Management',
    title: 'Master Data Management',
    breadcrumbs: [
      { label: 'Home', href: '/', icon: <HomeIcon /> },
      { label: 'Admin', href: '/admin', icon: <SettingsIcon /> },
      { label: 'Master Data Management', href: '/admin/mdm', icon: <StorageIcon /> },
    ],
  };

  // Initialize filter state BEFORE useMDMData to break circular dependency
  // This filter state is passed to useMDMData for cache-level filtering
  const { filters, searchTerm, handleFilterChange, handleSearchChange } = useMDMFilters(
    initialFieldDefId || '',
    initialTenantId || '',
    {
      include_inactive: true,
      include_deleted: false,
    }
  );

  // Use custom hooks for data and state management
  // Pass filters to useMDMData for client-side cache filtering
  const {
    context,
    fieldDefinitions,
    values,
    selectedFieldDef,
    selectedTenant,
    loading,
    tableLoading,
    switchingContext,
    error,
    setError,
    setSelectedFieldDef,
    handleTenantSwitch,
    refreshData,
  } = useMDMData(
    initialFieldDefId,
    initialTenantId,
    filters, // Pass filters to useMDMData for include_inactive/include_deleted filtering
    user?.activeTenant?.tenant_id
  );

  const {
    valueFormOpen,
    editingValue,
    openValueForm,
    closeValueForm,
    handleValueFormSuccess,
    importModalOpen,
    openImportModal,
    closeImportModal,
    handleImportSuccess,
    exportDialogOpen,
    openExportDialog,
    closeExportDialog,
    handleExportSuccess,
    revertDialogOpen,
    revertingValue,
    globalValue,
    revertLoading,
    openRevertDialog,
    closeRevertDialog,
    setGlobalValue,
    setRevertLoading,
  } = useMDMDialogs(refreshData);

  // Get filtered field definitions for dropdown
  const dropdownFieldDefinitions = useMemo(
    () => getDropdownFieldDefinitions(fieldDefinitions),
    [fieldDefinitions]
  );

  // Get selected field definition
  const selectedFieldDefinition = useMemo(
    () => fieldDefinitions.find((fd) => fd.fielddef_id === selectedFieldDef),
    [fieldDefinitions, selectedFieldDef]
  );

  // Check if field is source field
  const isSource = useMemo(() => isSourceField(selectedFieldDefinition), [selectedFieldDefinition]);

  // Get source field info for banner
  const sourceFieldInfo = (selectedFieldDefinition as any)?.source_field_definition;

  // Filter values based on search and filters
  const filteredValues = useMemo(
    () => filterValues(values, searchTerm, filters.scope),
    [values, searchTerm, filters.scope]
  );

  // Check if selection is valid
  const isValid = useMemo(
    () => isValidSelection(selectedFieldDef, selectedTenant),
    [selectedFieldDef, selectedTenant]
  );

  /**
   * Handle field definition change
   */
  const handleFieldDefChange = useCallback(
    (event: SelectChangeEvent) => {
      setSelectedFieldDef(event.target.value);
    },
    [setSelectedFieldDef]
  );

  /**
   * Handle create new value
   */
  const handleCreateValue = useCallback(() => {
    openValueForm();
  }, [openValueForm]);

  /**
   * Handle edit existing value
   */
  const handleEditValue = useCallback(
    (value: any) => {
      openValueForm(value);
    },
    [openValueForm]
  );

  /**
   * Handle delete value
   */
  const handleDeleteValue = useCallback(
    async (value: any) => {
      // Validate deletion permissions
      const { allowed, reason } = canDeleteValue(value, selectedTenant);

      if (!allowed) {
        alert(reason);
        return;
      }

      // Confirm deletion
      if (
        !window.confirm(
          `Are you sure you want to delete "${value.name}"? This action cannot be undone.`
        )
      ) {
        return;
      }

      try {
        await mdmService.deleteValue(value.id);
        refreshData();
      } catch (error: any) {
        console.error('Failed to delete value:', error);
        alert(error.message || 'Failed to delete value');
      }
    },
    [selectedTenant, refreshData]
  );

  /**
   * Handle activate/deactivate value
   */
  const handleToggleActive = useCallback(
    async (value: any) => {
      try {
        const updateData = {
          is_active: !value.is_active,
        };

        await mdmService.updateValue(value.id, updateData, selectedTenant);
        refreshData();
      } catch (error: any) {
        console.error('Failed to toggle value status:', error);
      }
    },
    [selectedTenant, refreshData]
  );

  /**
   * Handle revert value initiation
   */
  const handleRevertValue = useCallback(
    async (value: any) => {
      // Fetch the global value that will become visible
      if (value.global_value_id) {
        try {
          const globalVal = await mdmService.getValue(value.global_value_id);
          setGlobalValue(globalVal);
        } catch (error) {
          console.error('Failed to fetch global value:', error);
          setGlobalValue(null);
        }
      }

      openRevertDialog(value, null);
    },
    [openRevertDialog, setGlobalValue]
  );

  /**
   * Handle revert confirmation
   */
  const handleRevertConfirm = useCallback(async () => {
    if (!revertingValue) return;

    setRevertLoading(true);
    try {
      await mdmService.revertOverrideValue(revertingValue.id);
      closeRevertDialog();
      refreshData();
    } catch (error) {
      console.error('Failed to revert value:', error);
    } finally {
      setRevertLoading(false);
    }
  }, [revertingValue, closeRevertDialog, refreshData, setRevertLoading]);

  // Loading state
  if (loading) {
    return (
      <SkeletonPage {...pageConfig}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress size={60} />
        </Box>
      </SkeletonPage>
    );
  }

  return (
    <SkeletonPage {...pageConfig}>
      <Box>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Global Warning Banner */}
        {context?.current_tenant.is_global && (
          <DismissibleBanner severity="warning" icon={<GlobalIcon />} sx={{ mb: 2 }} useGlobalStyle>
            <Typography variant="subtitle1" component="div">
              You are managing GLOBAL master data
            </Typography>
            <Typography variant="body2">
              Changes to GLOBAL master data affect all tenants in the system. Please use caution
              when making modifications.
            </Typography>
          </DismissibleBanner>
        )}

        {/* Source Field Read-Only Banner */}
        {isSource && sourceFieldInfo && (
          <Alert
            severity="info"
            icon={<LinkIcon />}
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setSelectedFieldDef(sourceFieldInfo.fielddef_id)}
              >
                View Source Field
              </Button>
            }
          >
            <AlertTitle>Read-Only Field Definition</AlertTitle>
            This field displays filtered values from <strong>{sourceFieldInfo.label}</strong>.
            Values are read-only and cannot be edited here.
          </Alert>
        )}

        {/* Controls Section */}
        <MDMControls
          selectedFieldDef={selectedFieldDef}
          onFieldDefChange={handleFieldDefChange}
          selectedTenant={selectedTenant}
          onTenantChange={handleTenantSwitch}
          fieldDefinitions={dropdownFieldDefinitions}
          context={context}
          switchingContext={switchingContext}
          dataTestId="mdm-controls"
        />

        {/* Main Content - Value Table */}
        {isValid && (
          <MDMValueTable
            values={filteredValues}
            selectedFieldDefinition={selectedFieldDefinition}
            allFieldDefinitions={fieldDefinitions}
            selectedTenant={selectedTenant}
            activeTenantId={user?.activeTenant?.tenant_id}
            loading={tableLoading}
            error={error}
            onRowClick={handleEditValue}
            onRefresh={refreshData}
            onCreateValue={!isSource && canEditMdm ? handleCreateValue : undefined}
            onEditValue={handleEditValue}
            onDeleteValue={handleDeleteValue}
            onToggleActive={handleToggleActive}
            onRevertValue={handleRevertValue}
            onManageAliases={handleManageAliases}
            onOpenImport={can(PermissionKey.MDM_IMPORT) ? openImportModal : undefined}
            onOpenExport={can(PermissionKey.MDM_EXPORT) ? openExportDialog : undefined}
            customToolbarFilters={
              <MDMFilters
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                filters={filters}
                onFilterChange={handleFilterChange}
                dataTestId="mdm-filters"
              />
            }
            dataTestId="mdm-value-table"
          />
        )}

        {/* Modals and Dialogs */}
        {isValid && (
          <>
            <ValueFormModal
              open={valueFormOpen}
              onClose={closeValueForm}
              value={editingValue || undefined}
              fielddef_id={selectedFieldDef}
              tenant_id={selectedTenant}
              fieldDefinition={selectedFieldDefinition}
              onSuccess={handleValueFormSuccess}
              readOnly={isSource || !canEditMdm}
              onManageAliases={handleManageAliases}
            />

            <AliasManagerDrawer
              open={aliasDrawerOpen}
              onClose={handleCloseAliasDrawer}
              value={aliasDrawerValue}
              activeTenantId={selectedTenant}
              canManageGlobalAliases={canManageGlobalAliases}
              onChanged={refreshData}
            />

            <ImportModal
              open={importModalOpen}
              onClose={closeImportModal}
              fielddef_id={selectedFieldDef}
              tenant_id={selectedTenant}
              onImportComplete={handleImportSuccess}
            />

            <ExportDialog
              open={exportDialogOpen}
              onClose={closeExportDialog}
              fielddef_id={selectedFieldDef}
              tenant_id={selectedTenant}
              onExportComplete={handleExportSuccess}
            />

            <RevertOverrideDialog
              open={revertDialogOpen}
              onClose={closeRevertDialog}
              onConfirm={handleRevertConfirm}
              overrideValue={revertingValue}
              globalValue={globalValue}
              loading={revertLoading}
            />
          </>
        )}
      </Box>
    </SkeletonPage>
  );
};

export default MDMManagerPage;
